import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useKakaoMap } from "^/common/useKakaoMap";
import { twMerge } from "tailwind-merge";

declare global {
  interface Window {
    daum: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

let postcodeLoadPromise: Promise<void> | null = null;
const POSTCODE_SCRIPT_ID = "daum-postcode-sdk";
const POSTCODE_LOAD_TIMEOUT_MS = 12_000;

const loadPostcodeScript = (): Promise<void> => {
  if (postcodeLoadPromise) return postcodeLoadPromise;

  const attempt = new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("window is not available"));
      return;
    }
    if (window.daum?.Postcode) {
      resolve();
      return;
    }
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      callback();
    };
    const finishSuccess = () => finish(resolve);
    const finishError = (error: Error) => {
      const current = document.getElementById(
        POSTCODE_SCRIPT_ID
      ) as HTMLScriptElement | null;
      if (current && !window.daum?.Postcode) {
        current.dataset.loadState = "failed";
      }
      finish(() => reject(error));
    };
    const timeoutId = window.setTimeout(() => {
      finishError(new Error("Daum Postcode SDK load timed out"));
    }, POSTCODE_LOAD_TIMEOUT_MS);

    const existing = document.getElementById(
      POSTCODE_SCRIPT_ID
    ) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loadState !== "failed") {
        existing.addEventListener("load", finishSuccess, { once: true });
        existing.addEventListener(
          "error",
          () => finishError(new Error("Failed to load Daum Postcode SDK")),
          { once: true }
        );
        return;
      }
      existing.remove();
    }
    const script = document.createElement("script");
    script.id = POSTCODE_SCRIPT_ID;
    script.src =
      "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    script.dataset.loadState = "loading";
    script.onload = () => {
      script.dataset.loadState = "loaded";
      finishSuccess();
    };
    script.onerror = () => {
      script.dataset.loadState = "failed";
      finishError(new Error("Failed to load Daum Postcode SDK"));
    };
    document.head.appendChild(script);
  });

  postcodeLoadPromise = attempt;
  void attempt.catch(() => {
    if (postcodeLoadPromise === attempt) {
      postcodeLoadPromise = null;
    }
  });
  return attempt;
};

export interface PickedAddress {
  address: string; // 사용자가 선택한 대표 주소 (도로명 또는 지번)
  latitude: string;
  longitude: string;
}

interface BusinessAddressPickerProps {
  value?: string;
  onSelect: (picked: PickedAddress) => void;
  className?: string;
  placeholder?: string;
}

type Step = "closed" | "search" | "preview";

/**
 * 사업장 주소 선택기.
 * 자유 입력을 막고(readOnly) 다음 우편번호 검색 → 지오코딩 → 지도 위치 확인을
 * 하나의 흐름으로 강제해서 주소-좌표 불일치를 원천 차단한다.
 * onSelect는 사용자가 지도에서 위치를 확인하고 확정했을 때만 호출된다.
 */
const BusinessAddressPicker = ({
  value = "",
  onSelect,
  className,
  placeholder = "주소 검색을 눌러 선택하세요",
}: BusinessAddressPickerProps) => {
  const {
    ready: mapReady,
    error: mapError,
    retry: retryMap,
  } = useKakaoMap();
  const [step, setStep] = useState<Step>("closed");
  const [pending, setPending] = useState<PickedAddress | null>(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [geocodeTarget, setGeocodeTarget] = useState("");

  const postcodeBoxRef = useRef<HTMLDivElement>(null);
  const previewMapRef = useRef<HTMLDivElement>(null);

  // step === "search" 진입 시 우편번호 검색 위젯 embed
  useEffect(() => {
    if (step !== "search") return;
    let canceled = false;
    loadPostcodeScript()
      .then(() => {
        if (canceled || !postcodeBoxRef.current) return;
        postcodeBoxRef.current.innerHTML = "";
        new window.daum.Postcode({
          width: "100%",
          height: "100%",
          oncomplete: (data: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            const address =
              data.userSelectedType === "R"
                ? data.roadAddress
                : data.jibunAddress;
            setGeocodeFailed(false);
            setPending({ address, latitude: "", longitude: "" });
            setGeocodeTarget(data.roadAddress || address);
            setStep("preview");
          },
        }).embed(postcodeBoxRef.current);
      })
      .catch((error) => {
        if (!canceled) console.error("Failed to open address search", error);
      });
    return () => {
      canceled = true;
    };
  }, [step]);

  // 주소 선택이 SDK 로딩보다 빨라도 준비 완료 후 지오코딩한다.
  useEffect(() => {
    if (step !== "preview" || !pending || !geocodeTarget) return;
    if (pending.latitude && pending.longitude) return;
    if (mapError) {
      setGeocodeFailed(true);
      return;
    }
    if (!mapReady) return;

    let canceled = false;
    const selectedAddress = pending.address;
    const geocoder = new window.kakao.maps.services.Geocoder();
    geocoder.addressSearch(
      geocodeTarget,
      (result: any[], status: string) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (canceled) return;
        if (
          status !== window.kakao.maps.services.Status.OK ||
          !result[0]
        ) {
          setGeocodeFailed(true);
          return;
        }
        setPending((current) =>
          current?.address === selectedAddress
            ? {
                address: selectedAddress,
                latitude: result[0].y,
                longitude: result[0].x,
              }
            : current
        );
        setGeocodeFailed(false);
      }
    );

    return () => {
      canceled = true;
    };
  }, [step, pending, geocodeTarget, mapReady, mapError]);

  // step === "preview" + 좌표 확보 시 미니 지도에 마커 표시
  useEffect(() => {
    if (step !== "preview" || !mapReady || !previewMapRef.current) return;
    if (!pending?.latitude || !pending?.longitude) return;
    const kakao = window.kakao;
    const pos = new kakao.maps.LatLng(
      Number(pending.latitude),
      Number(pending.longitude)
    );
    const map = new kakao.maps.Map(previewMapRef.current, {
      center: pos,
      level: 3,
    });
    new kakao.maps.Marker({ map, position: pos });
  }, [step, mapReady, pending]);

  const handleConfirm = () => {
    if (!pending) return;
    onSelect(pending);
    setStep("closed");
    setPending(null);
    setGeocodeTarget("");
  };

  const handleClose = () => {
    setStep("closed");
    setPending(null);
    setGeocodeTarget("");
  };

  const handleOpenSearch = () => {
    if (mapError) retryMap();
    setGeocodeFailed(false);
    setPending(null);
    setGeocodeTarget("");
    setStep("search");
  };

  return (
    <div className={twMerge("w-full", className)}>
      <button
        type="button"
        onClick={handleOpenSearch}
        className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-left"
      >
        <span
          className={twMerge(
            "flex-1 truncate text-sm",
            value ? "text-black" : "text-gray-400"
          )}
        >
          {value || placeholder}
        </span>
        <span className="shrink-0 rounded-full bg-orange-400 px-3 py-1.5 text-xs font-semibold text-white">
          주소 검색
        </span>
      </button>

      {/* main(-webkit-overflow-scrolling 스크롤 컨테이너) 내부에 fixed를 두면
          iOS 웹뷰가 뷰포트 레이어로 승격하지 못해 네비/하단 버튼이 모달 위에
          그려진다. body 포털로 탈출시켜야 한다. (Dialog·BottomSheet와 동일 패턴) */}
      {step !== "closed" &&
        createPortal(
        <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/40">
          <div className="flex h-[85%] flex-col rounded-t-2xl bg-white">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-base font-bold">
                {step === "search" ? "주소 검색" : "위치 확인"}
              </span>
              <button
                type="button"
                onClick={handleClose}
                aria-label="닫기"
                className="p-1 text-xl leading-none text-gray-400"
              >
                ×
              </button>
            </div>

            {step === "search" && (
              <div ref={postcodeBoxRef} className="min-h-0 flex-1" />
            )}

            {step === "preview" && pending && (
              <div className="flex min-h-0 flex-1 flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                <div className="mb-3 rounded-lg bg-[#F5F5F5] px-4 py-3 text-sm font-medium">
                  {pending.address}
                </div>
                <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg bg-[#F5F5F5]">
                  <div ref={previewMapRef} className="absolute inset-0" />
                  {!pending.latitude && !geocodeFailed && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
                      위치 확인 중...
                    </div>
                  )}
                  {geocodeFailed && (
                    <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-gray-400">
                      지도에서 위치를 찾지 못했습니다.
                      <br />
                      저장 시 주소 기준으로 위치가 자동 설정됩니다.
                    </div>
                  )}
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleOpenSearch}
                    className="flex-1 rounded-full border border-gray-300 py-3 text-sm font-semibold"
                  >
                    다시 검색
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!pending.latitude && !geocodeFailed}
                    className="flex-1 rounded-full bg-orange-400 py-3 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    이 위치가 맞아요
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BusinessAddressPicker;
